import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import {
  GetMenusQuery,
  Maybe,
  Menu,
  NestedLink,
} from "../../graphql/gql/graphql";
import { getMenus } from "../api";

type MenuResponse = {
  menuData: Record<string, Menu | undefined>;
  isError: boolean;
  isLoading: boolean;
  getSportMenu: (sport: string) => Maybe<NestedLink> | undefined;
};

const useMenus = (
  menus: string[],
  initialData?: GetMenusQuery
): MenuResponse => {
  const { data, isError, isLoading } = useQuery({
    placeholderData: keepPreviousData,
    queryKey: ["menus", menus],
    queryFn: () => getMenus(),
    select: (response) => response.getMenus || [],
    initialData,
  });

  const menuData = useMemo(
    () =>
      data
        ? menus.reduce(
            (menuCollection, menu) => {
              const test =
                data.find(
                  (menuInfo) =>
                    menuInfo?.name?.toLowerCase() === menu.toLocaleLowerCase()
                ) || undefined;

              return {
                ...menuCollection,
                [menu]: test,
              };
            },
            {} as Record<string, Menu | undefined>
          )
        : {},
    [data, menus]
  );

  const getSportMenu = useCallback(
    (sport: string) =>
      menuData?.["Research Menu"]?.menuItems.find(
        (menu) => menu?.text.toLowerCase() === sport?.toLowerCase()
      ),
    [menuData]
  );

  return {
    menuData,
    isError,
    isLoading,
    getSportMenu,
  };
};

export const useNavMenus = (initialData?: GetMenusQuery) =>
  useMenus(NAV_MENUS, initialData);

export default useMenus;
export const NAV_MENUS = [
  "header",
  "Research Menu",
  "footer",
  "terms",
  "copyright",
  "Research Mobile",
];
